import * as React from 'react';

import type * as api from '@gitbook/api-client';
import { diffRevisionPages } from '@gitbook/hive-core';
import { useHiveClient, useHiveRevision } from '@gitbook/hive-react';
import { Badge, CircularProgress, Heading } from '@gitbook/spine-react';
import { getSuspense } from '@gitbook/util-suspense-cache';

import { ChangeRequestNoChanges } from './ChangeRequestNoChanges';
import { PageDiffPanel, PageDiffPanelSkeleton } from './PageDiffPanel';
import { useViewedChangesCount } from './viewedChanges';
import { WithChangeRequestRevision } from './WithChangeRequestRevision';

export type ChangeRequestChangesProps = ChangeRequestChangesListProps;

/**
 * Fetch and display all the pages that have been changed in the change request.
 */
export function ChangeRequestChanges(props: ChangeRequestChangesProps) {
    const { space, changeRequest, currentPageId } = props;

    return (
        <React.Suspense fallback={<ChangeRequestChangesSkeleton />}>
            <WithChangeRequestRevision space={space} changeRequest={changeRequest}>
                <ChangeRequestChangesList
                    space={space}
                    changeRequest={changeRequest}
                    currentPageId={currentPageId}
                />
            </WithChangeRequestRevision>
        </React.Suspense>
    );
}

type ChangeRequestChangesListProps = {
    space: api.Space;
    changeRequest: api.ChangeRequest;
    currentPageId: string | null;
};

/**
 * Display all the pages that have been changed in the task.
 */
function ChangeRequestChangesList(props: ChangeRequestChangesListProps) {
    const { space, changeRequest, currentPageId } = props;

    const { revision: currentRevision } = useHiveRevision();

    const hiveClient = useHiveClient();
    const baseRevision = getSuspense(
        hiveClient.store.assertRevision(
            space.id,
            changeRequest.revisionMergedAncestor ?? changeRequest.revisionInitial
        )
    );

    const changes = React.useMemo(() => {
        return diffRevisionPages(currentRevision, baseRevision);
    }, [currentRevision, baseRevision]);

    const pagesDiff = React.useMemo(() => {
        return Array.from(changes.diffs.entries()).sort((a, b) => {
            return a[0].localeCompare(b[0]);
        });
    }, [changes]);

    const viewedChangesCount = useViewedChangesCount({ changeRequestId: changeRequest.id });

    return (
        <div className="flex flex-col gap-2">
            <div className="flex flex-row items-center justify-between gap-2">
                <div className="flex flex-row items-center gap-2">
                    <Heading level={2} className="text-secondary text-ui-heading-small">
                        Changes
                    </Heading>
                    <Badge kind="secondary" size="xsmall" label={pagesDiff.length} />
                </div>
                {pagesDiff.length > 0 ? (
                    <div className="flex flex-row items-center gap-2">
                        <CircularProgress
                            segments={[
                                {
                                    value: (100 * viewedChangesCount) / pagesDiff.length,
                                    kind: 'done',
                                },
                            ]}
                            className="size-5"
                        />
                        <div className="text-muted text-ui-small tabular-nums">
                            <span className="text-base">{viewedChangesCount}</span>
                            {' / '}
                            <span className="text-base">{pagesDiff.length}</span>
                            {' viewed'}
                        </div>
                    </div>
                ) : null}
            </div>
            {pagesDiff.length === 0 ? (
                <ChangeRequestNoChanges space={space} changeRequest={changeRequest} />
            ) : (
                <div className="flex flex-col gap-4">
                    {pagesDiff.map(([pageKey, pageDiff], index) => {
                        return (
                            <PageDiffPanel
                                key={pageKey}
                                pageDiff={pageDiff}
                                organizationId={space.organization}
                                baseRevision={baseRevision}
                                currentRevision={currentRevision}
                                changeRequest={changeRequest}
                                defaultGenerated={
                                    index === 0 || currentPageId === pageDiff.item.page.id
                                }
                            />
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function ChangeRequestChangesSkeleton() {
    return (
        <div className="flex flex-col gap-2">
            <Heading level={2} className="text-secondary text-ui-heading-small">
                Changes
            </Heading>
            <div className="flex flex-col gap-6">
                <PageDiffPanelSkeleton />
                <PageDiffPanelSkeleton />
            </div>
        </div>
    );
}
